import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  formatMetricValue,
  formatPaymentDateLong,
} from 'src/components/LeadAgentAcceptanceBanner/LeadAgentAcceptanceBanner.functions';
import AcceptanceSuccessModal from 'src/components/LeadAgentAcceptanceBanner/modals/AcceptanceSuccessModal/AcceptanceSuccessModal.component';
import ConfirmTierModal from 'src/components/LeadAgentAcceptanceBanner/modals/ConfirmTierModal/ConfirmTierModal.component';
import type { tAcceptanceConfirmedResult } from 'src/components/LeadAgentAcceptanceBanner/modals/ConfirmTierModal/ConfirmTierModal.types';
import DeclineModal from 'src/components/LeadAgentAcceptanceBanner/modals/DeclineModal/DeclineModal.component';
import { useLeadAgentTierPricing } from 'src/components/LeadAgentAcceptanceBanner/useLeadAgentTierPricing.hook';
import { useLeadAgentAcceptanceContext } from 'src/providers/LeadAgentAcceptanceProvider/LeadAgentAcceptance.context';
import { resolveSuggestedTier } from 'src/providers/LeadAgentAcceptanceProvider/LeadAgentAcceptance.functions';
import { useSubscriptionContext } from 'src/providers/SubscriptionProvider/Subscription.context';
import { ePlanFrequency } from 'src/providers/SubscriptionProvider/Subscription.enums';
import type { tAlertDetailRendererProps } from './AlertDetail.registry';
import AlertDetailBody from './AlertDetailBody.component';
import AlertOfferLine from './AlertOfferLine.component';

/** The pre-acceptance banner's namespace — its copy moves here verbatim. */
const OFFER_NS = 'common/leadAgentAcceptanceBanner';

type tModalView = 'closed' | 'confirm' | 'success' | 'decline';

/**
 * Rich body for the Lead Agent pre-acceptance alert (RET-1956): the
 * recommended tier, the "nothing is charged today" reassurance and the REAL
 * accept/decline flows, adopted from `LeadAgentAcceptanceBannerContainer`
 * after RET-1879 turned that banner off.
 *
 * Same view machine the container shipped — confirm → success, decline in its
 * own modal — mounted as NESTED modals so the host detail refuses to dismiss
 * itself mid-flow (REL-2, the `LeadAgentTrialCapDetail` pattern). The machine
 * is what keeps the confirmation on screen after the PATCH resolves the alert
 * underneath it: both flows write state that flips `isBannerVisible` false.
 *
 * IMPACT METRICS — the banner's `miniStats`, adopted verbatim. An earlier pass
 * dropped them here on the claim that the numbers "arrive with a future backend
 * snapshot". That was wrong, and checking would have shown it: the chain is
 * live today. `lead_agent_stats_rollups` in workspace-service recomputes daily
 * (10,779 rows, last written the morning this was verified, 2026-09-01); the
 * presale row carries them in `precalculated_data`; the BFF maps them to
 * camelCase; and this provider already parses them into `acceptance.metrics`.
 * 352 of 792 workspaces hold real values — 82 of 83 accepted, 241 of 679 still
 * deciding.
 *
 * `miniStats` and NOT the four big cards on purpose: the banner shipped both,
 * and the three-stat row IS its compact variant, built for exactly this kind of
 * narrow space. Reusing the tested one beats inventing a third.
 *
 * Each stat drops itself when its metric is absent and the row disappears when
 * none survive, so the workspaces without a rollup degrade to what was here
 * before rather than printing zeros that would understate the agent's work.
 */
export default function LeadAgentAcceptanceDetail(
  props: tAlertDetailRendererProps,
) {
  const { t } = useTranslation(OFFER_NS);
  const { acceptance, isBannerVisible, bannerVariant } =
    useLeadAgentAcceptanceContext();
  const { subscription } = useSubscriptionContext();

  const [view, setView] = useState<tModalView>('closed');
  const [confirmedResult, setConfirmedResult] =
    useState<tAcceptanceConfirmedResult | null>(null);

  const { onNestedModalChange } = props;
  useEffect(() => {
    onNestedModalChange(view !== 'closed');
    return () => onNestedModalChange(false);
  }, [view, onNestedModalChange]);

  const suggestedTier = resolveSuggestedTier(acceptance?.suggestedTier);
  const { price } = useLeadAgentTierPricing({
    workspaceId: acceptance?.workspaceId ?? '',
    tier: suggestedTier,
    frequency: subscription?.frequency ?? ePlanFrequency.MONTHLY,
    currency: subscription?.currency ?? '',
    enabled: isBannerVisible,
  });

  const paymentDateLong = formatPaymentDateLong(
    acceptance?.nextPaymentAt ?? null,
  );

  // The banner's compact stat row, same three metrics and same filter: a metric
  // the rollup did not produce removes its own stat rather than rendering a
  // dash or a zero.
  const metrics = acceptance?.metrics;
  const miniStats = [
    {
      key: 'engagedLeads',
      value: formatMetricValue(metrics?.engagedLeadsCount ?? null),
      label: t('miniStats.engagedLeads'),
    },
    {
      key: 'messages',
      value: formatMetricValue(metrics?.leadAgentMessagesCount ?? null),
      label: t('miniStats.messages'),
    },
    {
      key: 'outOfHours',
      value: formatMetricValue(metrics?.outOfHoursAttendedLeadsCount ?? null),
      label: t('miniStats.outOfHours'),
    },
  ].filter((stat) => stat.value !== null);

  function handleConfirmed(result: tAcceptanceConfirmedResult) {
    setConfirmedResult(result);
    setView('success');
  }

  return (
    <div className="AlertDetail" data-testid="alert-detail-la-acceptance">
      <AlertDetailBody alert={props.alert} />

      {/* The offer block and its actions follow the OFFER, not the view: the
          modals below are gated on `view` alone so a flow that resolves the
          alert mid-way keeps its confirmation step on screen (the legacy
          container's rule). */}
      {isBannerVisible ? (
        <>
          {/* MLA-81 — the catalog price whole, nothing struck through. No
              price at all means the catalog has not landed, and the block does
              not render. */}
          <AlertOfferLine
            testId="alert-detail-acceptance-reco"
            label={t('reco.label')}
            plan={t('reco.plan', { tier: t(`tier.${suggestedTier}`) })}
            price={price ?? null}
          />

          {/* What the agent has ALREADY done, above the price: it is the
              argument the offer rests on, and the reason this is a decision
              rather than an upsell. Hidden entirely when no metric survived. */}
          {miniStats.length > 0 ? (
            <div
              className="AlertDetail__MiniStats"
              data-testid="alert-detail-acceptance-ministats"
            >
              {miniStats.map((stat) => (
                <span key={stat.key} className="AlertDetail__MiniStat">
                  <b>{stat.value}</b> {stat.label}
                </span>
              ))}
            </div>
          ) : null}

          {/* Before renewal this is a deferred acceptance. Once renewal has
              passed, the same CTA enters the immediate prorated-charge flow,
              so the detail must not keep promising a $0 action. */}
          <p
            className="AlertDetail__Note"
            data-testid="alert-detail-acceptance-reassure"
          >
            {acceptance?.phase === 'after_renewal'
              ? t('reassureChargeNow')
              : paymentDateLong
                ? t('reassure', { date: paymentDateLong })
                : t('reassureNoDate')}
          </p>

          {/* One row, as the prototype's `.actions`: accepting and declining
              are the two answers to the same question. Declining stays the
              lightest thing in it. */}
          <div className="AlertDetail__Actions">
            <button
              type="button"
              className="AlertDetail__OptionCta AlertDetail__OptionCta--primary"
              onClick={() => setView('confirm')}
              data-testid="alert-detail-acceptance-accept"
            >
              {/* Urgency-scoped exactly as the banner scoped it. */}
              {t(`${bannerVariant}.primaryCta`)}
            </button>
            <button
              type="button"
              className="AlertDetail__OptionCta AlertDetail__OptionCta--link"
              onClick={() => setView('decline')}
              data-testid="alert-detail-acceptance-decline"
            >
              {t('declineCta')}
            </button>
          </div>
        </>
      ) : null}

      {view === 'confirm' ? (
        <ConfirmTierModal
          onClose={() => setView('closed')}
          onConfirmed={handleConfirmed}
        />
      ) : null}

      {view === 'decline' ? (
        <DeclineModal
          onClose={() => setView('closed')}
          onReactivate={() => setView('confirm')}
        />
      ) : null}

      {view === 'success' && confirmedResult ? (
        <AcceptanceSuccessModal
          tier={confirmedResult.tier}
          renewalTotal={confirmedResult.renewalTotal}
          chargeDate={confirmedResult.chargeDate}
          onClose={() => setView('closed')}
        />
      ) : null}
    </div>
  );
}
